import { Component, OnDestroy, OnInit } from '@angular/core'
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router'
import { AutomaticOrdersSettings, CoreService, NotificationService, ProductMdClassService } from '../../services'
import { File, Product, ProductPrice, Template } from 'depoto-core/src/entities'
import { SchemaUtil, getThumbUrl as imageGetThumbUrl } from '../../utils'
import { BaseDetail } from '../_base-detail/base-detail'
import { ProductDetailService } from '../../services/product-detail.service'
import { ProductCreatingService } from '../../services/product-creating.service'
import * as Sentry from '@sentry/angular-ivy'
import { TranslateService } from '@ngx-translate/core'
import { CompanyUtilService } from '../../services/company-util.service'
import { ScrollService } from '../../services/scroll.service'

@Component({
  selector: 'app-product',
  templateUrl: './product.component.html',
  styleUrls: ['product.component.scss'],
})
export class ProductComponent extends BaseDetail implements OnInit, OnDestroy {
  activeLink = ''
  canMoveProducts = false

  isDuplicatedProduct = false
  selectedTemplate: Template & { mdClass?: string }
  parentToCreate: Partial<Product>

  isProductTemplateSelectionPage = false

  mainImageToCreate
  public isBatchlessModeEnabled = true
  automaticOrdersSettings: AutomaticOrdersSettings
  constructor(
    public route: ActivatedRoute,
    public router: Router,
    public core: CoreService,
    private notificationService: NotificationService,
    public productDetailService: ProductDetailService,
    public productCreatingService: ProductCreatingService,
    public translateService: TranslateService,
    public readonly scrollService: ScrollService,
    private readonly companyUtilService: CompanyUtilService,
    public readonly productMdClassService: ProductMdClassService,
  ) {
    super(route, router, core, translateService, scrollService)
    this.scrollService.setKey(this.core.services.storage.keys.views.productList) // we need to reset product list on create / update / delete..
    this.setActiveLink()
    // todo check if needed here. looks like we don't. test needed.
    // 2025/10/22 we need this for selecting template, after navigating back from already selected one. todo check if this breaks something else
    // 2025/11/03 - update url.includes() from 'product' to 'product/common', as this is not needed on existing products
    router.events.subscribe(val => {
      if (val instanceof NavigationEnd && val.url?.includes('product/common')) {
        // console.log('product detail navEnd:setActiveLink', { val })
        this.setActiveLink()
      }
    })
    this.isBatchlessModeEnabled = this.companyUtilService.getBatchlessMode()
    this.automaticOrdersSettings = this.core.getAutomaticOrdersSettings()
  }

  async ngOnInit() {
    await this.reloadViewDataFromRoute()
    await this.setActiveLink()
    this.productDetailService.getRoles()
    this.productCreatingService.clearOptions()
    this.productCreatingService.clearImageToCreate()
    // await this.reloadViewDataFromRoute() // moved before setActiveLink. todo test
    this.canMoveProducts = this.hasRoleForMoveProducts()
    this.productDetailService.getCoreEntityLists()
  }

  ngOnDestroy() {
    const nextId = Number(this.router.url.split('/')[2])
    if (this.productDetailService.product?.id !== nextId) {
      this.productDetailService.product = undefined
      this.productDetailService.selectedMdClass = ''
    }
  }

  async setActiveLink() {
    // reload data if routing to parent or variant
    const urlParts = window.location.href.split('/')
    // console.log(urlParts)
    if (
      !!this.product?.id &&
      urlParts[urlParts.length - 2] === 'product' &&
      Number(this.router.url.split('/')[2]) !== this.product?.id
    ) {
      if (this.router.url.split('/')[3] !== '') this.activeLink = this.router.url.split('/')[3]
      else this.activeLink = 'common'
      // console.log('productId, but different from url. activeLink:', this.activeLink)
      await this.reloadViewDataFromRoute()
    }

    if (this.productDetailService.isCreating) {
      // console.log('isCreating')
      if (this.router.url.split('/')[2] === undefined || this.router.url.split('/')[2] === 'undefined') {
        // console.log('isCreating. no ID in url, create new product && set common as activeLink')
        this.activeLink = 'common'
        this.isProductTemplateSelectionPage = true
        this.productDetailService.product = new Product({
          enabled: true,
          purchaseCurrency: this.core.services.user.user?.company?.currency,
          ...((this.core.services.user.user?.company?.batchExpirationMode === 'enabled-everywhere' ||
            this.isBatchlessModeEnabled) && {
            enabledBatchExpiration: true,
          }),
        })
      } else {
        // console.log('isCreating. something in url on product/xxx: ', String(this.router.url.split('/')[2]))
        this.activeLink = String(this.router.url.split('/')[2])
        // this.triggerNextStep() // this fixes weird middle step when creating/updating supplier before moving here and creating product. todo - test if it doesnot introduce other magic!
        // 2025/11/03 - yep, this also fucks up navigation from productlist, after selecting template even once.. all products with ids then load as new creation template..
        // todo, find another way to fix supplier create/update issue
      }
    } else {
      // console.log('is NOT Creating')
      if (this.router.url.split('/')[3] === undefined) {
        this.activeLink = 'common'
        // console.log('is NOT Creating. NOTHING in url on product/id/xxx: ', String(this.router.url.split('/')[3]))
      } else {
        this.activeLink = String(this.router.url.split('/')[3])
        // console.log('is NOT Creating. SOMETHING in url on product/id/xxx: ', String(this.router.url.split('/')[3]))
      }
    }
  }

  private async reloadViewDataFromRoute() {
    const id = Number(this.router.url.split('/')[2])
    if (isNaN(id)) {
      this.productDetailService.isCreating = true
      this.isProductTemplateSelectionPage = true
      this.productDetailService.product = new Product({
        enabled: true,
        purchaseCurrency: this.core.services.user.user?.company?.currency,
        ...((this.core.services.user.user?.company?.batchExpirationMode === 'enabled-everywhere' ||
          this.isBatchlessModeEnabled) && {
          enabledBatchExpiration: true,
        }),
      })
      this.productDetailService.loading = false
      return
    }
    if (this.route.snapshot.routeConfig.path.includes('product-variant-create') && !isNaN(id)) {
      this.productDetailService.isCreating = true
      this.productDetailService.product = new Product({
        enabled: true,
        purchaseCurrency: this.core.services.user.user.company.currency,
        ...((this.core.services.user.user.company.batchExpirationMode === 'enabled-everywhere' ||
          this.isBatchlessModeEnabled) && {
          enabledBatchExpiration: true,
        }),
      })
      this.product.parent = this.productDetailService.product.parent = await this.core.services.product.getById(
        id,
        SchemaUtil.product,
      )
      await this.refreshData()
    } else {
      this.productDetailService.isCreating = false
      await this.refreshData(id)
    }
  }

  showProductDepotsPage() {
    return (
      this.product?.id > 0 &&
      (!this.product.children.length ||
        (this.product.children.length &&
          this.productDetailService.productMoves &&
          this.productDetailService.productMoves.length))
    )
  }

  a() {
    // todo veronike? wut & why
    // console.log(this.isBatchlessModeEnabled)
    // console.log(this.activeLink)
    // console.log(this.activeLink === 'common')
    // console.log(this.router.url.includes('common'))
    // console.log(this.isBatchlessModeEnabled && this.activeLink === 'common')
    return this.isBatchlessModeEnabled && this.router.url.includes('common')
  }

  public async refreshData(id?: number): Promise<any> {
    this.productDetailService.loading = true
    this.productDetailService.getCoreEntityLists()
    this.productDetailService.getProductPack()
    if (
      this.productDetailService.isCreating &&
      !this.product &&
      !this.isDuplicatedProduct &&
      !this.productDetailService.isProductFromTemplate
    ) {
      this.productDetailService.product = new Product({
        enabled: true,
        purchaseCurrency: this.core.services.user.user.company.currency,
        tags: this.productDetailService.tags.filter(tag => tag.externalId === 'hs-count-by-piece'),
        ...((this.core.services.user.user.company.batchExpirationMode === 'enabled-everywhere' ||
          this.isBatchlessModeEnabled) && {
          enabledBatchExpiration: true,
        }),
      })
    } else if (this.productDetailService.isCreating) {
      if (this.productDetailService.isProductFromTemplate) await this.convertTemplateToProduct()
      else {
        this.productDetailService.product = new Product({
          enabled: true,
          purchaseCurrency: this.core.services.user.user.company.currency,
          tags: this.productDetailService.tags.filter(tag => tag.externalId === 'hs-count-by-piece'),
          ...((this.core.services.user.user.company.batchExpirationMode === 'enabled-everywhere' ||
            this.isBatchlessModeEnabled) && {
            enabledBatchExpiration: true,
          }),
        })
      }
      this.isProductTemplateSelectionPage = false
      this.activeLink = 'common'
      await this.router.navigate(['/product/common'])
    }
    if (!this.productDetailService.isCreating) {
      try {
        this.productDetailService.product = await this.productDetailService.getProduct(id)
      } catch (e) {
        console.log(e)
        if (e && typeof e.includes === 'function' && e.includes('Error 403: Forbidden.')) {
          return this.onLoadError(403)
        }
        return this.onLoadError(404)
      }
      if (!this.product) {
        return this.onLoadError(404)
      }
      this.productDetailService.product.advancedPrices = [...(this.product.advancedPrices || [])].map(
        advancedPrice =>
          new ProductPrice({
            ...advancedPrice,
            dateFrom: this.formatDateStringFromDateTime(advancedPrice.dateFrom),
            dateTo: this.formatDateStringFromDateTime(advancedPrice.dateTo),
          }),
      )
      if (this.isDuplicatedProduct) {
        delete this.product.id
        if (this.product.children?.length) {
          this.product.children.forEach((a, i) => {
            delete this.product.children[i].id
          })
        }
      } else {
        this.productDetailService.formatStock()
      }

      if (this.product.tags.some(tag => tag.externalId === 'hs-count-by-piece')) {
        await this.productDetailService.getProductPack()
      } else {
        this.productDetailService.isCountByPiece = false
      }
    }
    this.productDetailService.loading = false
  }

  triggerBackBtn() {
    if (this.productDetailService.isCreating && !this.isProductTemplateSelectionPage) {
      this.isProductTemplateSelectionPage = true
      this.productDetailService.product = new Product({
        enabled: true,
        purchaseCurrency: this.core.services.user.user.company.currency,
        ...((this.core.services.user.user.company.batchExpirationMode === 'enabled-everywhere' ||
          this.isBatchlessModeEnabled) && {
          enabledBatchExpiration: true,
        }),
      })
    } else {
      if (window.history.length > 1) {
        window.history.back()
      } else {
        return this.router.navigate(['/products'])
      }
    }
  }

  public async triggerNextStep() {
    if (!this.productDetailService.isProductFromTemplate) {
      this.productCreatingService.clearOptions()
      this.productCreatingService.clearImageToCreate()
      this.mainImageToCreate = null
    }
    await this.refreshData()
  }

  async productTemplateSelected(template) {
    this.selectedTemplate = template
    this.productDetailService.isProductFromTemplate = !!template
    if (template?.mdClass) {
      this.productDetailService.selectedMdClass = this.productMdClassService.normalizeMdClass(template.mdClass)
    }
    this.productCreatingService.clearOptions()
    this.mainImageToCreate = this.productCreatingService.getFirstImage(template)
    this.productCreatingService.mainImageToCreate = this.mainImageToCreate
    if (template?.parent?.mCode) {
      this.parentToCreate = template.parent
    }
    if (template.children?.length > 0) {
      template.children = await this.productCreatingService.checkIfChildrenExist(
        template.children,
        this.productDetailService.vats,
        this.productDetailService.suppliers,
      )
    }
    if (
      this.core.services.user.user.company.batchExpirationMode === 'enabled-everywhere' ||
      this.isBatchlessModeEnabled
    ) {
      this.productDetailService.product.enabledBatchExpiration = true
      setTimeout(() => {
        this.productDetailService.product.enabledBatchExpiration = true
      }, 500) // after navigation hack..
    }
    if (!template) {
      return
    }
    await this.refreshData()
    this.triggerNextStep()
  }

  public async resolveStockLoad() {
    try {
      const p = await this.core.services.product.getById(this.productDetailService.product.id, {
        id: null,
        productDepots: {
          id: null,
          depot: {
            id: null,
            name: null,
          },
          supplier: {
            id: null,
            name: null,
          },
          purchasePrice: null,
          quantityStock: null,
          inventoryQuantityStock: null,
          batch: null,
          expirationDate: null,
          position: null,
          position1: null,
          position2: null,
          position3: null,
          created: null,
          updated: null,
        },
      })
      this.productDetailService.product.productDepots = p.productDepots
      this.productDetailService.formatStock()
      this.scrollService.save('products', [])
    } catch (err) {
      console.log(err)
    }
  }

  async convertTemplateToProduct() {
    if (this.selectedTemplate) {
      this.productDetailService.piecesInPack = this.selectedTemplate.packQuantity
      if (this.selectedTemplate.packQuantity > 1) {
        this.productDetailService.isCountByPiece = true
      }
      this.productDetailService.product = await this.productCreatingService.getProductValues(
        this.selectedTemplate,
        false,
        this.productDetailService.vats,
      )
      this.productDetailService.selectedMdClass =
        this.productMdClassService.normalizeMdClass(this.selectedTemplate.mdClass) ||
        this.productMdClassService.getCurrentValue(this.productDetailService.product)
    }
  }

  public async createProduct(): Promise<void> {
    this.productDetailService.submitted = true
    if (!(this.product?.name?.length > 0)) {
      alert(this.translateService.instant('product.enter-name'))
      return
    }
    if (this.parentToCreate) {
      this.productDetailService.product.parent = await this.productCreatingService.createParentIfNotExists(
        this.parentToCreate,
        this.productDetailService.vats,
        this.productDetailService.suppliers,
      )
      this.parentToCreate = undefined
    }
    try {
      this.productDetailService.loading = true
      if (!this.product?.vat?.id) this.product.vat.id = 1
      this.product.enabled = true

      if (this.core.services.user.user.company.batchExpirationMode === 'enabled-everywhere') {
        this.productDetailService.product.enabledBatchExpiration = true
      }
      if (
        this.productDetailService.product?.customData &&
        typeof this.productDetailService.product?.customData !== 'string'
      ) {
        try {
          this.productDetailService.product.customData = JSON.stringify(this.productDetailService.product?.customData)
        } catch (e) {
          console.log(e)
        }
      }
      // this.productDetailService.product.sellPrice = 0
      const createSchema = {
        id: null,
        name: null,
        ean: null,
        code: null,
        tags: {
          id: null,
          externalId: null,
        },
      }
      this.notificationService.avoidErrorAlerts = true
      let result = await this.core.services.product.create(this.productDetailService.product, createSchema)
      // duplicate EAN / similar create error: retry without EANs so the product and image can still be saved
      if (!result) {
        result = await this.core.services.product.create(
          {
            ...this.productDetailService.product,
            ean: undefined,
            ean2: undefined,
            ean3: undefined,
            ean4: undefined,
            ean5: undefined,
          },
          createSchema,
        )
      }
      this.notificationService.avoidErrorAlerts = false
      if (!result) {
        return
      }
      if (result?.id) {
        this.productDetailService.product = result
        if (this.productDetailService.product.tags?.some(tag => tag?.externalId === 'hs-count-by-piece')) {
          await this.createOrUpdateProductPack()
        }
        await this.productMdClassService.save(result, this.productDetailService.selectedMdClass)
      }
      const imageToCreate = this.getImageToCreate()
      if (result?.id && imageToCreate) {
        await this.productCreatingService.createImage(imageToCreate, result.id)
      }
      if (result?.id && this.productCreatingService.childrenWithImagesToCreate?.length > 0) {
        for (const childAndImage of this.productCreatingService.childrenWithImagesToCreate) {
          childAndImage.childToCreate.parent = result
          try {
            if (this.core.services.user.user.company.batchExpirationMode === 'enabled-everywhere') {
              childAndImage.childToCreate.enabledBatchExpiration = true
            }
            const createdChild = await this.core.services.product.create(
              childAndImage.childToCreate,
              SchemaUtil.product,
            )
            if (this.productDetailService.product.tags?.some(tag => tag?.externalId === 'hs-count-by-piece')) {
              await this.createOrUpdateProductPack()
            }
            const childMdClass =
              this.productMdClassService.getCurrentValue(childAndImage.childToCreate) ||
              this.productDetailService.selectedMdClass
            if (createdChild?.id && childMdClass) {
              await this.productMdClassService.save(createdChild, childMdClass)
            }
            if (childAndImage.imageToCreate) {
              await this.productCreatingService.createImage(childAndImage.imageToCreate, createdChild.id)
            }
          } catch (err) {
            console.log(err)
          }
        }
      }
      if (result?.id) {
        this.notificationService.success(this.translateService.instant('alert.product.added'))
        this.productDetailService.isCreating = false
        this.productCreatingService.clearImageToCreate()
        this.mainImageToCreate = null
        this.scrollService.save('products', [])
        await this.router.navigate(['/product', result.id])
      }
    } catch (e) {
      console.log(e)
    } finally {
      this.notificationService.avoidErrorAlerts = false
      if (this.productDetailService.isCreating) {
        this.productDetailService.submitted = false
        this.productDetailService.loading = false
      }
    }
  }

  private getImageToCreate() {
    return (
      this.mainImageToCreate ||
      this.productCreatingService.mainImageToCreate ||
      this.productCreatingService.getFirstImage(this.selectedTemplate)
    )
  }

  async createOrUpdateProductPack() {
    if (!this.productDetailService.productPack?.id) {
      try {
        const result = await this.core.services.productPack.getList(
          { filters: { product: this.product.id } },
          SchemaUtil.productPack,
        )
        this.productDetailService.productPack = result?.items?.length > 0 ? result.items[0] : null
      } catch (err) {
        console.log(err)
      }
    }
    if (this.productDetailService.productPack?.id > 0) {
      this.productDetailService.productPack.quantity = this.productDetailService.piecesInPack
      await this.core.services.productPack.update(this.productDetailService.productPack)
    } else {
      const pack = {
        id: null,
        product: new Product({
          id: this.product.id,
          purchaseCurrency: this.core.services.user.user.company.currency,
          ...((this.core.services.user.user.company.batchExpirationMode === 'enabled-everywhere' ||
            this.isBatchlessModeEnabled) && {
            enabledBatchExpiration: true,
          }),
        }),
        quantity: this.productDetailService.piecesInPack,
        externalId: 'hs-count-by-piece',
        ean: this.product.ean,
        code: this.product.code,
        note: undefined,
        weight: undefined,
      }
      await this.core.services.productPack.create(pack, SchemaUtil.productPack)
    }
    this.scrollService.save('products', [])
  }

  public async updateProduct(): Promise<void> {
    this.productDetailService.submitted = true
    if (this.product?.name?.length < 1 && !this.product?.sellPrice) {
      this.productDetailService.submitted = false
      return
    }
    this.productDetailService.loading = true
    if (!this.productDetailService.product.tags?.some(tag => tag?.externalId === 'count-by-piece')) {
      this.createOrUpdateProductPack()
    }
    try {
      await this.productMdClassService.save(this.product, this.productDetailService.selectedMdClass)
      this.productDetailService.product = await this.core.services.product.update(this.product, SchemaUtil.product)
      this.productDetailService.initMdClass()
      this.notificationService.success(this.translateService.instant('alert.product.saved'))
      this.scrollService.save('products', [])
    } catch (e) {
      console.log(e)
    } finally {
      this.productDetailService.loading = false
    }
  }

  public async deleteProduct(): Promise<void> {
    if (!confirm(this.translateService.instant('product.delete.confirmation'))) {
      return new Promise(r => r())
    }
    this.productDetailService.loading = true
    try {
      this.productDetailService.product = await this.core.services.product.delete(this.product)
      this.notificationService.success(this.translateService.instant('alert.product.deleted'))
    } catch (e) {
      console.log(e)
    }
    this.productDetailService.loading = false
    this.scrollService.save('products', [])
    await this.router.navigate(['/products'])
  }

  private formatDateStringFromDateTime(dateTime: string): string[] | string {
    return !!dateTime && dateTime.length > 0 ? dateTime.split(' ')[0] : ''
  }

  private hasRoleForMoveProducts(): boolean {
    const rolesForMoveProducts = [
      'ROLE_SUPER_ADMIN',
      'ROLE_ADMIN',
      'ROLE_CERT_LIST',
      'ROLE_SHOP_LIST',
      'ROLE_PRODUCT_MOVE_CREATE',
      'ROLE_EET_RECEIPT_LIST',
    ]
    return this.core.services.user.hasOneOfTheRoles(rolesForMoveProducts)
  }

  getThumbUrl(image: File): string {
    return imageGetThumbUrl(image)
  }

  get product() {
    return this.productDetailService.product
  }
}
